import React from 'react';
import {
  Box, Chip, Paper, Stack, Typography,
} from '@mui/material';
import InvoiceDetailSearchBar from './InvoiceDetailSearchBar';
import PaymentInvoiceDetailTable from './PaymentInvoiceDetailTable';

const Metric = ({ label, value, color = 'text.primary' }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.66rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', color }}>
      {value}
    </Typography>
  </Box>
);

const buildAlertChips = (alerts = {}) => {
  const chips = [];
  if (alerts.shortage) chips.push({ key: 'shortage', label: 'Faltantes', color: 'warning' });
  if (alerts.priceDiff) chips.push({ key: 'priceDiff', label: 'Dif. precio', color: 'error' });
  if (alerts.promotional) chips.push({ key: 'promotional', label: 'Promocional', color: 'secondary' });
  if (alerts.credit) chips.push({ key: 'credit', label: 'NC', color: 'info' });
  if (alerts.previousPayment) chips.push({ key: 'prev', label: 'Abono previo', color: 'default' });
  if (alerts.review) chips.push({ key: 'review', label: 'Revisar', color: 'default' });
  return chips;
};

export default function PaymentInvoiceDetailPanel({
  invoice,
  metrics,
  alerts,
  rows,
  query,
  status,
  onQueryChange,
  onStatusChange,
  paginationModel,
  onPaginationModelChange,
  formatCurrency,
}) {
  if (!invoice) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minHeight: 420 }}>
        <Typography variant="body2" color="text.secondary">
          Seleccione una factura para ver el detalle.
        </Typography>
      </Paper>
    );
  }

  const chips = buildAlertChips(alerts || {});

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 1.25,
        borderColor: alerts?.review ? 'warning.light' : 'divider',
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={0.75}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
              Factura #{invoice.number}{invoice.series ? ` - ${invoice.series}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {invoice.supplier || '-'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {chips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                color={chip.color}
                size="small"
                sx={{ height: 19, '& .MuiChip-label': { px: 0.7, fontSize: '0.64rem' } }}
              />
            ))}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          flexWrap="wrap"
          sx={{
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'grey.50',
          }}
        >
          <Metric label="Saldo original" value={formatCurrency(metrics?.originalBalance || 0)} />
          <Metric label="Ajuste decimal" value={formatCurrency(metrics?.decimalAdjustment || 0)} color="warning.main" />
          <Metric label="Abonos" value={formatCurrency(metrics?.previousPayments || 0)} color="info.main" />
          <Metric label="N. credito" value={formatCurrency(metrics?.creditNotes || 0)} color="secondary.main" />
          <Metric label="Ajustes sug." value={formatCurrency(metrics?.suggestedAdjustments || 0)} color="success.dark" />
          <Metric label="Ajustes aplic." value={formatCurrency(metrics?.appliedAdjustments || 0)} color="success.main" />
          <Metric label="Pago final" value={formatCurrency(metrics?.finalPayable || 0)} color="primary.main" />
        </Stack>

        <InvoiceDetailSearchBar
          query={query}
          status={status}
          onQueryChange={onQueryChange}
          onStatusChange={onStatusChange}
        />

        <Box sx={{ height: 470 }}>
          <PaymentInvoiceDetailTable
            rows={rows}
            formatCurrency={formatCurrency}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
          />
        </Box>
      </Stack>
    </Paper>
  );
}
